import React, { useCallback, type ForwardedRef, useState } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { HierarchyToggle$key } from '@atlassian/jira-relay/src/__generated__/HierarchyToggle.graphql';
import type { HierarchyToggleOldMutation } from '@atlassian/jira-relay/src/__generated__/HierarchyToggleOldMutation.graphql';
import type { HierarchyToggleMutation } from '@atlassian/jira-relay/src/__generated__/HierarchyToggleMutation.graphql';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../common/constants.tsx';
import messages from './messages.tsx';

export type HierarchyToggleProps = {
	viewResult: HierarchyToggle$key;
	forwardRef?: ForwardedRef<HTMLInputElement>;
	isGroupingSupported?: boolean;
	onChangeHierarchy: (isHierarchyEnabled: boolean) => void;
};

export const HierarchyToggle = ({
	viewResult,
	forwardRef,
	isGroupingSupported,
	onChangeHierarchy,
}: HierarchyToggleProps) => {
	const { formatMessage } = useIntl();

	const viewResultData = useFragment<HierarchyToggle$key>(
		graphql`
			fragment HierarchyToggle on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				staticViewInput: { type: "JiraIssueSearchStaticViewInput" }
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				id
				viewId
				savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						isHierarchyEnabled
						canEnableHierarchy
						isGroupingEnabled
						canEnableGrouping
					}
				}
			}
		`,
		viewResult,
	);

	const { viewSettings, id, viewId } = viewResultData;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const toggleIssueHierarchy = useUpdateHierarchyMutation();
	const { isGroupingEnabled, canEnableGrouping, isHierarchyEnabled, canEnableHierarchy } =
		viewSettings ?? {};

	const [checked, setChecked] = useState(isHierarchyEnabled);

	const updateHierarchy = useCallback(
		(hierarchyPreference: boolean) => {
			setChecked(hierarchyPreference);
			onChangeHierarchy && onChangeHierarchy(hierarchyPreference);
		},
		[onChangeHierarchy],
	);

	const onChange = useCallback(() => {
		let viewAri = id;
		if (fg('saved_list_view_technical_changes')) {
			if (!viewResultData.savedViewId) {
				throw new Error('Missing saved view ID');
			}
			viewAri = viewResultData.savedViewId;
		}

		if (viewAri) {
			fireUIAnalytics(createAnalyticsEvent({}), 'hierarchyToggle clicked', {
				isHierarchyEnabled: !isHierarchyEnabled,
			});
			toggleIssueHierarchy({ viewAri, isHierarchyEnabled: !isHierarchyEnabled, updateHierarchy });
		}
	}, [
		viewResultData.savedViewId,
		id,
		createAnalyticsEvent,
		isHierarchyEnabled,
		toggleIssueHierarchy,
		updateHierarchy,
	]);

	const isGrouping = isGroupingSupported && isGroupingEnabled && canEnableGrouping;

	return (
		<Stack>
			<Inline spread="space-between" alignBlock="center">
				<label htmlFor={`${viewId}_hierarchy_toggle`}>
					{formatMessage(messages.showHierarchy)}
				</label>
				<Toggle
					isDisabled={!canEnableHierarchy}
					isChecked={Boolean(checked && canEnableHierarchy)}
					id={`${viewId}_hierarchy_toggle`}
					ref={forwardRef}
					onChange={onChange}
					size={fg('jsc-1730-adjust_toggle_size') ? undefined : 'large'}
				/>
			</Inline>
			{!canEnableHierarchy && (
				<Text color="color.text.subtlest" size="small">
					{isGrouping
						? formatMessage(messages.hierarchyDisabledReasonGroupingAndFilters)
						: formatMessage(messages.hierarchyDisabledReasonFilters)}
				</Text>
			)}
		</Stack>
	);
};

export const useUpdateHierarchyMutation = () => {
	const isAnonymous = useIsAnonymous();

	const [commitOld] = useMutation<HierarchyToggleOldMutation>(graphql`
		mutation HierarchyToggleOldMutation($viewId: ID!, $isHierarchyEnabled: Boolean!) {
			jira {
				updateIssueSearchHierarchyPreference(
					viewId: $viewId
					isHierarchyEnabled: $isHierarchyEnabled
				) @optIn(to: "JiraUpdateIssueSearchHierarchyPreference") {
					success
				}
			}
		}
	`);

	const [commit] = useMutation<HierarchyToggleMutation>(graphql`
		mutation HierarchyToggleMutation($input: JiraSetIssueSearchHierarchyEnabledInput!) {
			jira_setIssueSearchHierarchyEnabled(input: $input) {
				success
				errors {
					message
				}
			}
		}
	`);
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const commitMutation = useCallback(
		({
			viewAri,
			isHierarchyEnabled,
			updateHierarchy,
		}: {
			viewAri: string;
			isHierarchyEnabled: boolean;
			updateHierarchy: (isHierarchyEnabled: boolean) => void;
		}) => {
			if (isAnonymous) {
				updateHierarchy(isHierarchyEnabled);

				return;
			}

			if (fg('saved_list_view_technical_changes')) {
				commit({
					variables: {
						input: {
							viewId: viewAri,
							hierarchyEnabled: isHierarchyEnabled,
						},
					},
					onError: (error) => {
						fireErrorAnalytics({
							meta: {
								id: 'issue-table-hierarchy-toggle',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							error,
						});

						showFlag({
							id: 'hierarchyToggleError',
							title: formatMessage(messages.hierarchyErrorFlagTitle),
							description: formatMessage(messages.hierarchyErrorFlagDescription),
							isAutoDismiss: true,
							type: 'error',
							messageId: 'issue-table-hierarchy-toggle.ui.show-flag.error.hierarchy-toggle-error',
							messageType: 'transactional',
						});
					},
				});
				updateHierarchy(isHierarchyEnabled);
			} else {
				commitOld({
					variables: {
						viewId: viewAri,
						isHierarchyEnabled,
					},
					onError: (error) => {
						fireErrorAnalytics({
							meta: {
								id: 'issue-table-hierarchy-toggle',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							error,
						});

						showFlag({
							id: 'hierarchyToggleError',
							title: formatMessage(messages.hierarchyErrorFlagTitle),
							description: formatMessage(messages.hierarchyErrorFlagDescription),
							isAutoDismiss: true,
							type: 'error',
							messageId: 'issue-table-hierarchy-toggle.ui.show-flag.error.hierarchy-toggle-error.1',
							messageType: 'transactional',
						});
					},
				});
				updateHierarchy(isHierarchyEnabled);
			}
		},
		[commit, commitOld, formatMessage, isAnonymous, showFlag],
	);

	return commitMutation;
};
